// src/components/EmailThreadPanel.tsx
// Полноценный почтовый блок в сделке — цепочки писем (threads)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, Send, RefreshCw, ChevronDown, ChevronRight, Paperclip,
  Eye, CheckCircle, XCircle, X, FileText, Reply, ReplyAll, Inbox, Download,
} from 'lucide-react';
import api from '../api/client';
import { useToast } from '../contexts/ToastContext';

interface Props {
  dealId: string;
  clientEmail?: string;
  clientName?: string;
  onUnreadChange?: (count: number) => void;
}

interface EmailMessage {
  _id: string;
  direction: 'incoming' | 'outgoing';
  from: string;
  fromName?: string;
  to: string[];
  subject: string;
  textBody: string;
  htmlBody: string;
  messageId?: string;
  inReplyTo?: string;
  references?: string[];
  threadId?: string;
  seen?: boolean;
  flagged?: boolean;
  createdAt: string;
  sentAt?: string;
  attachments?: any[];
  status?: string;
  opened?: boolean;
  openCount?: number;
}

interface EmailThread {
  threadId: string;
  subject: string;
  messages: EmailMessage[];
  messageCount: number;
  unreadCount: number;
  lastMessageAt: string;
  lastFrom: string;
  lastDirection: string;
  lastPreview: string;
}

const EmailThreadPanel: React.FC<Props> = ({ dealId, clientEmail, clientName, onUnreadChange }) => {
  const { showToast } = useToast();
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [expandedMsg, setExpandedMsg] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyToMsg, setReplyToMsg] = useState<EmailMessage | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);

  const onUnreadChangeRef = useRef(onUnreadChange);
  useEffect(() => { onUnreadChangeRef.current = onUnreadChange; }, [onUnreadChange]);

  // ★ load(silent=true) — без спиннера (для polling)
  const load = useCallback(async (silent: boolean = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/crm/email/deal/${dealId}/thread`);
      const list: EmailThread[] = r.data.threads || [];
      setThreads(prev => {
        // ★ Сохраняем state expanded (если только не первый рендер)
        return list;
      });
      const unread = r.data.totalUnread || 0;
      setTotalUnread(unread);
      onUnreadChangeRef.current?.(unread);
    } catch (e) { /* silent */ }
    finally { if (!silent) setLoading(false); }
  }, [dealId]);

  // Первая загрузка (с индикатором)
  useEffect(() => { load(false); }, [load]);

  // ★ Polling каждые 15 сек — БЕЗ спиннера, без мигания
  useEffect(() => {
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  // ★ Отметить письмо прочитанным
  const markAsRead = useCallback(async (msgId: string) => {
    try {
      await api.put(`/crm/email/messages/${msgId}`, { seen: true });
      setThreads(prev => {
        let removedUnread = 0;
        const next = prev.map(th => {
          const hasIt = th.messages.some(m => m._id === msgId);
          if (!hasIt) return th;
          const wasUnread = th.messages.find(m => m._id === msgId)?.direction === 'incoming'
                         && !th.messages.find(m => m._id === msgId)?.seen;
          if (wasUnread) removedUnread++;
          return {
            ...th,
            messages: th.messages.map(m => m._id === msgId ? { ...m, seen: true } : m),
            unreadCount: Math.max(0, th.unreadCount - (wasUnread ? 1 : 0)),
          };
        });
        if (removedUnread > 0) {
          setTotalUnread(prevUn => {
            const nextUn = Math.max(0, prevUn - removedUnread);
            onUnreadChangeRef.current?.(nextUn);
            return nextUn;
          });
        }
        return next;
      });
    } catch (e) { /* silent */ }
  }, []);

  // Открыть тред — раскрыть + пометить первое непрочитанное
  const openThread = (thread: EmailThread) => {
    const isSame = expandedThread === thread.threadId;
    setExpandedThread(isSame ? null : thread.threadId);
    if (!isSame) {
      // Помечаем все непрочитанные входящие прочитанными
      thread.messages
        .filter(m => m.direction === 'incoming' && !m.seen)
        .forEach(m => markAsRead(m._id));
      // Раскрываем последнее письмо
      const last = thread.messages[thread.messages.length - 1];
      if (last) setExpandedMsg(last._id);
    }
  };

  const handleReply = (msg: EmailMessage) => {
    setReplyToMsg(msg);
    setComposeOpen(true);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'только что';
    if (mins < 60) return `${mins} мин`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} ч`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} дн`;
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div style={{ padding: '16px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Mail size={18} color="#06b6d4" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 }}>
            Переписка
            {totalUnread > 0 && (
              <span style={{
                background: '#ef4444', color: '#fff',
                fontSize: 10, fontWeight: 800, padding: '2px 8px',
                borderRadius: 10,
              }}>{totalUnread} новых</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {threads.length > 0
              ? `${threads.length} ${threads.length === 1 ? 'цепочка' : 'цепочек'} · ${threads.reduce((s, t) => s + t.messageCount, 0)} писем · ${clientEmail || '—'}`
              : (clientEmail ? `Писем пока нет · ${clientEmail}` : 'Нет переписки')}
          </div>
        </div>
        <button onClick={() => load(false)} disabled={loading} style={btnTiny} title="Обновить">
          <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </button>
        <button
          onClick={() => { setReplyToMsg(null); setComposeOpen(true); }}
          style={btnPrimary}
        >
          <Send size={12} /> Написать
        </button>
      </div>

      {/* Content */}
      {loading && threads.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <div style={{ marginTop: 8, fontSize: 12 }}>Загрузка переписки...</div>
        </div>
      ) : threads.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <Inbox size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
          <div style={{ fontSize: 13, fontWeight: 600 }}>Нет переписки</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Начните с кнопки «Написать»</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {threads.map(th => (
            <ThreadCard
              key={th.threadId}
              thread={th}
              expanded={expandedThread === th.threadId}
              expandedMsg={expandedMsg}
              onOpenThread={() => openThread(th)}
              onToggleMsg={(id) => setExpandedMsg(prev => prev === id ? null : id)}
              onReply={handleReply}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* Compose */}
      {composeOpen && (
        <ComposeModal
          dealId={dealId}
          defaultTo={clientEmail || ''}
          replyToMsg={replyToMsg}
          onClose={() => { setComposeOpen(false); setReplyToMsg(null); }}
          onSent={() => { setComposeOpen(false); setReplyToMsg(null); load(); }}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
//  Карточка треда (цепочки)
// ═══════════════════════════════════════════════════════
const ThreadCard: React.FC<{
  thread: EmailThread;
  expanded: boolean;
  expandedMsg: string | null;
  onOpenThread: () => void;
  onToggleMsg: (id: string) => void;
  onReply: (msg: EmailMessage) => void;
  formatDate: (s: string) => string;
}> = ({ thread, expanded, expandedMsg, onOpenThread, onToggleMsg, onReply, formatDate }) => {
  const hasUnread = thread.unreadCount > 0;
  return (
    <div style={{
      border: hasUnread ? '1px solid rgba(6,182,212,0.3)' : '1px solid #e2e8f0',
      borderRadius: 12,
      background: hasUnread ? 'rgba(6,182,212,0.04)' : '#fff',
      overflow: 'hidden',
    }}>
      {/* Header треда */}
      <button
        onClick={onOpenThread}
        style={{
          width: '100%', padding: '12px 14px',
          background: 'transparent', border: 'none',
          cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'flex-start', gap: 10,
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: hasUnread ? '#06b6d4' : 'rgba(100,116,139,0.1)',
          color: hasUnread ? '#fff' : '#64748b',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: 13, fontWeight: 800,
        }}>
          {thread.messageCount}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <div style={{
              fontSize: 13, fontWeight: hasUnread ? 800 : 700,
              color: '#1a1a2e', flex: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {thread.subject}
            </div>
            {hasUnread && (
              <span style={{
                background: '#ef4444', color: '#fff',
                fontSize: 9, fontWeight: 800, padding: '2px 6px',
                borderRadius: 8, flexShrink: 0,
              }}>{thread.unreadCount}</span>
            )}
            <div style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
              {formatDate(thread.lastMessageAt)}
            </div>
          </div>
          <div style={{
            fontSize: 11, color: '#64748b',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            <b>{thread.lastDirection === 'incoming' ? '← ' : '→ '}</b>
            {thread.lastFrom} · {thread.lastPreview || '(пусто)'}
          </div>
        </div>
        <div style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </button>

      {/* Развёрнутый тред — все письма */}
      {expanded && (
        <div style={{
          borderTop: '1px solid rgba(0,0,0,0.05)',
          padding: '8px 10px',
          background: 'rgba(0,0,0,0.015)',
        }}>
          {thread.messages.map((msg, idx) => (
            <MessageItem
              key={msg._id}
              msg={msg}
              expanded={expandedMsg === msg._id}
              onToggle={() => onToggleMsg(msg._id)}
              onReply={() => onReply(msg)}
              formatDate={formatDate}
              isLast={idx === thread.messages.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
//  Письмо внутри треда
// ═══════════════════════════════════════════════════════
const MessageItem: React.FC<{
  msg: EmailMessage;
  expanded: boolean;
  onToggle: () => void;
  onReply: () => void;
  formatDate: (s: string) => string;
  isLast: boolean;
}> = ({ msg, expanded, onToggle, onReply, formatDate, isLast }) => {
  const isIncoming = msg.direction === 'incoming';
  const isUnread = isIncoming && !msg.seen;
  return (
    <div style={{ marginBottom: isLast ? 0 : 4 }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', padding: '8px 10px',
          background: isUnread ? 'rgba(6,182,212,0.08)' : 'transparent',
          border: 'none', borderRadius: 8,
          cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
        }}
      >
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          background: isIncoming ? 'rgba(6,182,212,0.15)' : 'rgba(16,185,129,0.15)',
          color: isIncoming ? '#0891b2' : '#059669',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: 12,
        }}>{isIncoming ? '📥' : '📤'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: isUnread ? 700 : 600,
            color: '#1a1a2e',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {isIncoming ? (msg.fromName || msg.from) : `→ ${(msg.to || []).join(', ')}`}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
            {formatDate(msg.sentAt || msg.createdAt)}
            {!isIncoming && msg.status === 'sent' && <> · ✓ Отправлено</>}
            {!isIncoming && msg.opened && <> · 👁 Прочитано</>}
            {msg.attachments && msg.attachments.length > 0 && <> · 📎 {msg.attachments.length}</>}
          </div>
        </div>
        <div style={{ color: '#94a3b8', flexShrink: 0 }}>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '8px 10px 12px 40px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
          {msg.htmlBody ? (
            <iframe
              srcDoc={msg.htmlBody}
              sandbox=""
              style={{
                width: '100%', minHeight: 120, border: 'none',
                background: '#fff', borderRadius: 8,
              }}
              onLoad={(e) => {
                const iframe = e.target as HTMLIFrameElement;
                try {
                  const h = iframe.contentDocument?.body?.scrollHeight || 120;
                  iframe.style.height = Math.max(120, h + 20) + 'px';
                } catch (err) { /* ignore */ }
              }}
            />
          ) : (
            <pre style={{
              margin: 0, padding: 10, background: '#fff', borderRadius: 8,
              fontSize: 12, color: '#1a1a2e', whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
            }}>{msg.textBody || '(пустое письмо)'}</pre>
          )}

          {msg.attachments && msg.attachments.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {msg.attachments.map((a: any, i: number) => (
                <button
                  key={i}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                      const res = await fetch(`/api/crm/email/messages/${msg._id}/attachments/${i}`, {
                        headers: { Authorization: 'Bearer ' + (token || '') },
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({ error: 'HTTP ' + res.status }));
                        throw new Error(err.error || 'Ошибка');
                      }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = a.filename || 'file';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    } catch (err: any) {
                      alert('Не удалось скачать: ' + (err?.message || 'ошибка'));
                    }
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 10px',
                    background: 'rgba(6,182,212,0.08)',
                    border: '1px solid rgba(6,182,212,0.25)',
                    borderRadius: 8,
                    fontSize: 11, color: '#0891b2',
                    cursor: 'pointer', fontFamily: 'inherit',
                    fontWeight: 600,
                  }}
                  title="Скачать файл"
                >
                  <FileText size={11} /> {a.filename}
                  <Download size={11} style={{ opacity: 0.7 }} />
                </button>
              ))}
            </div>
          )}

          {/* Кнопки действий */}
          <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
            <button onClick={(e) => { e.stopPropagation(); onReply(); }} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 12px',
              background: 'rgba(6,182,212,0.1)', color: '#0891b2',
              border: 'none', borderRadius: 8,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              <Reply size={11} /> Ответить
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
//  Модалка отправки
// ═══════════════════════════════════════════════════════
const ComposeModal: React.FC<{
  dealId: string;
  defaultTo: string;
  replyToMsg?: EmailMessage | null;
  onClose: () => void;
  onSent: () => void;
}> = ({ dealId, defaultTo, replyToMsg, onClose, onSent }) => {
  const { showToast } = useToast();
  const replyFrom = replyToMsg?.direction === 'incoming' ? replyToMsg.from : null;
  const replySubject = replyToMsg?.subject
    ? (/^re:/i.test(replyToMsg.subject) ? replyToMsg.subject : 'Re: ' + replyToMsg.subject)
    : '';
  const [to, setTo] = useState(replyFrom || defaultTo);
  const [subject, setSubject] = useState(replySubject);
  const [htmlBody, setHtmlBody] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const MAX_FILES = 10;

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const idx = result.indexOf(',');
        resolve(idx >= 0 ? result.substring(idx + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const tooBig = picked.filter(f => f.size > MAX_FILE_SIZE);
    if (tooBig.length) {
      showToast('Файл больше 20 МБ: ' + tooBig.map(f => f.name).join(', '), 'error');
    }
    const valid = picked.filter(f => f.size <= MAX_FILE_SIZE);
    const next = [...files, ...valid].slice(0, MAX_FILES);
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    (async () => {
      try {
        const a = await api.get('/crm/email/accounts');
        const items = a.data.items || [];
        setAccounts(items);
        const def = items.find((x: any) => x.isDefault && x.isActive);
        if (def) setAccountId(def._id);
        else if (items.length) setAccountId(items[0]._id);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleSend = async () => {
    if (!to.trim()) { showToast('Укажите получателя', 'error'); return; }
    if (!subject.trim()) { showToast('Укажите тему', 'error'); return; }
    if (!htmlBody.trim()) { showToast('Введите текст', 'error'); return; }
    if (!accountId) { showToast('Не настроен почтовый ящик', 'error'); return; }

    setSending(true);
    try {
      const payload: any = {
        accountId, to: [to.trim()],
        subject, htmlBody,
        textBody: htmlBody.replace(/<[^>]+>/g, ''),
        dealId,
      };
      // ★ Сохранение цепочки при ответе
      if (replyToMsg) {
        if (replyToMsg.messageId) payload.inReplyTo = replyToMsg.messageId;
        // references = все предыдущие + messageId
        const prevRefs: string[] = Array.isArray(replyToMsg.references) ? replyToMsg.references : [];
        const refs: string[] = [
          ...prevRefs,
          ...(replyToMsg.messageId ? [String(replyToMsg.messageId)] : []),
        ].filter(Boolean);
        if (refs.length) payload.references = refs;
      }
      if (files.length) {
        try {
          payload.attachments = await Promise.all(files.map(async f => ({
            filename: f.name,
            mimeType: f.type || 'application/octet-stream',
            size: f.size,
            content: await fileToBase64(f),
            contentEncoding: 'base64',
          })));
        } catch (err) {
          showToast('Ошибка чтения файлов', 'error');
          setSending(false);
          return;
        }
      }
      await api.post('/crm/email/send', payload);
      showToast('✅ Письмо отправлено', 'success');
      onSent();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Ошибка отправки', 'error');
    } finally { setSending(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
              {replyToMsg ? 'Ответ на письмо' : 'Новое письмо'}
            </div>
            <h2 style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 800 }}>
              {replyToMsg ? replyToMsg.subject : 'Написать письмо'}
            </h2>
          </div>
          <button onClick={onClose} style={btnIconMini}><X size={18} /></button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', maxHeight: '70vh' }}>
          <div>
            <label style={labelStyle}>От кого</label>
            <select value={accountId} onChange={e => setAccountId(e.target.value)} style={inputStyle}>
              {accounts.map(a => <option key={a._id} value={a._id}>{a.email}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Кому</label>
            <input value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Тема</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Сообщение</label>
            <textarea
              value={htmlBody}
              onChange={e => setHtmlBody(e.target.value)}
              style={{ ...inputStyle, minHeight: 200, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Текст письма..."
            />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Вложения {files.length > 0 && '(' + files.length + ')'}</label>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                style={{ ...btnTiny, fontSize: 11 }} disabled={files.length >= MAX_FILES}>
                <Paperclip size={11} /> Прикрепить файл
              </button>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
            </div>
            {files.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {files.map((f, i) => (
                  <div key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 10px', background: 'rgba(6,182,212,0.08)',
                    border: '1px solid rgba(6,182,212,0.2)',
                    borderRadius: 8, fontSize: 11, color: '#0891b2',
                  }}>
                    <FileText size={11} />
                    <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ color: '#94a3b8' }}>({(f.size / 1024).toFixed(0)} КБ)</span>
                    <button type="button" onClick={() => removeFile(i)} style={{
                      background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                      color: '#ef4444', display: 'inline-flex', alignItems: 'center',
                    }}><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
            {files.length === 0 && (
              <div style={{ fontSize: 11, color: '#94a3b8', padding: '8px 0' }}>
                Максимум {MAX_FILES} файлов, до 20 МБ каждый
              </div>
            )}
          </div>
        </div>

        <div style={modalFooter}>
          <button onClick={onClose} style={btnSecondary}>Отмена</button>
          <button onClick={handleSend} disabled={sending} style={btnPrimary}>
            <Send size={12} /> {sending ? 'Отправка...' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Стили ───
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 20 };
const modal: React.CSSProperties = { background: '#fff', borderRadius: 20, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const modalHeader: React.CSSProperties = { padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const modalFooter: React.CSSProperties = { padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#f8fafc' };
const btnPrimary: React.CSSProperties = { background: 'linear-gradient(135deg, #06b6d4, #0891b2)', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit' };
const btnSecondary: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#475569', fontFamily: 'inherit' };
const btnTiny: React.CSSProperties = { background: 'rgba(6,182,212,0.08)', color: '#0891b2', border: 'none', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' };
const btnIconMini: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 6, display: 'inline-flex', borderRadius: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6, textTransform: 'uppercase' };

export default EmailThreadPanel;
